import * as React from "react";
import { useHistory } from "react-router-dom";

import { PageWrapper } from "@/components/PageWrapper";
import { HeathStats } from "./components/HealthStats";
import { Leaderboards } from "./components/Leaderboards";
import { useAccountSettings } from "@/utils";
import { WebComponents } from "@/interfaces";
import { useMemo } from "react";
import { Spinner } from "@/components/ui";
import { webComponents } from "@/components/SideBar/components";

export const Dashboard: React.FC = () => {
  const history = useHistory();
  const { hasReadPermission, accountPermissionsLoading } = useAccountSettings();
  const hasDashboardStats = useMemo(() => {
    return hasReadPermission(WebComponents.DASHBOARD_STATS);
  }, [hasReadPermission]);

  const allowedComponents = useMemo(() => {
    return webComponents.filter(component => hasReadPermission(component.componentIds));
  }, [hasReadPermission]);

  return (
    <PageWrapper hideSeparator className="coachDashboard">
      {accountPermissionsLoading ? (
        <div className="flex justify-center items-center grow min-h-[400px]">
          <Spinner className="size-12 text-primary" />
        </div>
      ) : hasDashboardStats ? (
        <>
          <HeathStats />
          <Leaderboards />
        </>
      ) : (
        <div className="flex flex-col items-center grow min-h-[500px] w-full py-8" data-testid="welcome-empty-state">
          <div className="w-full max-w-5xl px-4 mb-8">
            <div className="w-full p-6 rounded-2xl bg-gradient-to-r from-card to-muted/15 border border-border/40 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-6">
              <div className="flex flex-col sm:flex-row items-center gap-5 text-center sm:text-left">
                <div className="flex flex-col items-center sm:items-start gap-1">
                  <span className="text-[10px] font-bold tracking-widest uppercase text-muted-foreground/85">
                    Welcome to
                  </span>
                  <img
                    src="https://cdn.socialcoach.io/socialcoachlogodark.png"
                    className="h-7 object-contain"
                    alt="SocialCoach Logo"
                  />
                </div>

                <div className="hidden sm:block h-8 w-px bg-border/60" />

                <p className="text-sm text-muted-foreground leading-relaxed max-w-md">
                  To get started, explore your active tools and features below or use the menu on the left.
                </p>
              </div>

              <div className="flex items-center gap-2 px-3.5 py-1.5 rounded-lg bg-muted text-xs text-muted-foreground font-semibold border border-border/30 shrink-0">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                </span>
                Let's build your social presence!
              </div>
            </div>
          </div>

          {allowedComponents.length > 0 && (
            <div className="w-full max-w-5xl px-4 flex flex-col gap-6" data-testid="allowed-components-grid">
              <div className="flex items-center gap-3">
                <hr className="grow border-border/40" />
                <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground/60 whitespace-nowrap">
                  Your Active Features
                </span>
                <hr className="grow border-border/40" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
                {allowedComponents.map(component => {
                  const ComponentIcon = component.icon;
                  return (
                    <div
                      key={component.path}
                      data-testid={`component-card-${component.title}`}
                      onClick={() => history.push(component.path)}
                      className="group p-6 rounded-xl border border-border/60 bg-card hover:bg-muted/30 hover:border-primary/40 hover:shadow-md transition-all duration-300 cursor-pointer text-left flex flex-col justify-between h-full min-h-[170px]"
                    >
                      <div>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                            <ComponentIcon className="w-5 h-5" />
                          </div>
                          <h3 className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors duration-200">
                            {component.title}
                          </h3>
                        </div>
                        <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{component.description}</p>
                      </div>

                      <div className="flex items-center gap-1.5 text-xs text-primary font-semibold mt-4 opacity-70 group-hover:opacity-100 transition-opacity duration-200">
                        Explore {component.title}
                        <span className="transform group-hover:translate-x-1 transition-transform duration-200">→</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </PageWrapper>
  );
};
